import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { RecordPicker } from '../../components/RecordPicker.js';

interface CategoryOption {
  readonly categoryId: string;
  readonly categoryCode: string;
  readonly categoryName: string;
}

interface ReportDefinitionRow {
  readonly reportDefinitionId: string;
  readonly reportCode: string;
  readonly reportName: string;
  readonly categoryName: string;
  readonly outputFormat: string;
  readonly status: string;
}

interface NewReportForm {
  reportCode: string;
  reportName: string;
  categoryId: string;
  sourceQuery: string;
}

const EMPTY_FORM: NewReportForm = { reportCode: '', reportName: '', categoryId: '', sourceQuery: '' };

/** L16.01 — Report catalog / Report Builder (`/reports`). Lists tenant report
 *  definitions and provides create/delete authoring (M4) plus navigation to the
 *  reporting sub-centres and persona dashboards (M3/M5). */
export function M16ReportCatalogPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<ReportDefinitionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<NewReportForm>(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  async function load(): Promise<void> {
    setRows(await apiRequest<ReportDefinitionRow[]>('/v1/m16/reports/definitions', { token }));
  }

  useEffect(() => {
    let alive = true;
    apiRequest<ReportDefinitionRow[]>('/v1/m16/reports/definitions', { token })
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  function startEdit(row: ReportDefinitionRow): void {
    setEditingId(row.reportDefinitionId);
    setForm({ reportCode: row.reportCode, reportName: row.reportName, categoryId: '', sourceQuery: '' });
  }

  function cancelEdit(): void {
    setEditingId(null);
    setForm(EMPTY_FORM);
  }

  async function saveReport(): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      if (editingId) {
        await apiRequest(`/v1/m16/reports/definitions/${editingId}`, {
          method: 'PUT',
          token,
          body: { reportName: form.reportName },
        });
        setEditingId(null);
      } else {
        await apiRequest('/v1/m16/reports/definitions', {
          method: 'POST',
          token,
          body: {
            reportCode: form.reportCode,
            reportName: form.reportName,
            categoryId: form.categoryId,
            sourceQuery: form.sourceQuery,
            columnDefinitions: [],
          },
        });
      }
      setForm(EMPTY_FORM);
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function deleteReport(id: string): Promise<void> {
    setBusy(true);
    setActionError(null);
    try {
      await apiRequest(`/v1/m16/reports/definitions/${id}`, { method: 'DELETE', token });
      await load();
    } catch (e) {
      setActionError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p role="alert">{error}</p>;
  if (!rows) return <p>{t('m16.catalog.loading')}</p>;

  // CreateReportDefinitionDto requires reportCode and reportName (min 3 each),
  // a categoryId and a sourceQuery. Editing sends only reportName, so only that
  // is required then. Mirrors the DTO so the screen never offers a save the API
  // must reject.
  const canSaveReport = editingId
    ? form.reportName.trim().length >= 3
    : form.reportCode.trim().length >= 3 &&
      form.reportName.trim().length >= 3 &&
      form.categoryId.trim().length > 0 &&
      form.sourceQuery.trim().length > 0;

  return (
    <main aria-label="report-catalog">
      <h1>{t('m16.catalog.title')}</h1>

      <nav aria-label="reporting-nav">
        <Link to="/reports/export" aria-label="nav-export">{t('m16.catalog.navExport')}</Link>
        {' · '}
        <Link to="/reports/mis" aria-label="nav-mis">{t('m16.catalog.navMis')}</Link>
        {' · '}
        <Link to="/reports/analytics" aria-label="nav-analytics">{t('m16.catalog.navAnalytics')}</Link>
        {' · '}
        <Link to="/reports/subscriptions" aria-label="nav-scheduler">{t('m16.catalog.navScheduler')}</Link>
        {' | '}
        <Link to="/dashboards/persona/executive" aria-label="nav-persona-executive">{t('m16.catalog.navExecutive')}</Link>
        {' · '}
        <Link to="/dashboards/persona/operational" aria-label="nav-persona-operational">{t('m16.catalog.navOperations')}</Link>
        {' · '}
        <Link to="/dashboards/persona/financial" aria-label="nav-persona-financial">{t('m16.catalog.navFinance')}</Link>
        {' · '}
        <Link to="/dashboards/persona/vendor" aria-label="nav-persona-vendor">{t('m16.catalog.navVendor')}</Link>
        {' · '}
        <Link to="/dashboards/persona/painter" aria-label="nav-persona-painter">{t('m16.catalog.navPainter')}</Link>
        {' · '}
        <Link to="/dashboards/persona/compliance" aria-label="nav-persona-compliance">{t('m16.catalog.navCompliance')}</Link>
      </nav>

      <section aria-label="report-builder">
        <h2>{editingId ? t('m16.catalog.editReportDef') : t('m16.catalog.newReportDef')}</h2>
        <input aria-label="new-report-code" data-testid="new-report-code" placeholder={t('m16.catalog.placeholderCode')} disabled={!!editingId}
          value={form.reportCode} onChange={(e) => setForm((f) => ({ ...f, reportCode: e.target.value }))} />
        <input aria-label="new-report-name" data-testid="new-report-name" placeholder={t('m16.catalog.placeholderName')}
          value={form.reportName} onChange={(e) => setForm((f) => ({ ...f, reportName: e.target.value }))} />
        <RecordPicker<CategoryOption>
          endpoint="/v1/m16/reports/categories"
          idField="categoryId"
          label={t('m16.catalog.placeholderCategory')}
          testId="new-report-category"
          value={form.categoryId}
          optionLabel={(c) => `${c.categoryName} (${c.categoryCode})`}
          onPick={(id) => setForm((f) => ({ ...f, categoryId: id }))}
        />
        <input aria-label="new-report-query" data-testid="new-report-query" placeholder={t('m16.catalog.placeholderQuery')}
          value={form.sourceQuery} onChange={(e) => setForm((f) => ({ ...f, sourceQuery: e.target.value }))} />
        <button type="button" data-testid="create-report-btn" disabled={busy || !canSaveReport} onClick={saveReport}>
          {editingId ? t('m16.catalog.saveBtn') : t('m16.catalog.createBtn')}
        </button>
        {editingId && <button type="button" data-testid="cancel-edit-btn" onClick={cancelEdit}>{t('m16.catalog.cancelBtn')}</button>}
      </section>
      {actionError && <p role="alert">{actionError}</p>}

      <table aria-label="report-catalog-table">
        <thead>
          <tr><th>{t('m16.catalog.colCode')}</th><th>{t('m16.catalog.colName')}</th><th>{t('m16.catalog.colCategory')}</th><th>{t('m16.catalog.colFormat')}</th><th>{t('m16.catalog.colStatus')}</th><th>{t('m16.catalog.colRun')}</th><th>{t('m16.catalog.colEdit')}</th><th>{t('m16.catalog.colDelete')}</th></tr>
        </thead>
        <tbody>
          {rows.map((d) => (
            <tr key={d.reportDefinitionId}>
              <td>{d.reportCode}</td>
              <td>{d.reportName}</td>
              <td>{d.categoryName}</td>
              <td>{d.outputFormat}</td>
              <td>{d.status}</td>
              <td>
                <Link to={`/reports/runner/${d.reportDefinitionId}`} aria-label={`run-${d.reportCode}`}>{t('m16.catalog.runLink')}</Link>
              </td>
              <td>
                <button type="button" aria-label={`edit-${d.reportCode}`} disabled={busy}
                  onClick={() => startEdit(d)}>{t('m16.catalog.editBtn')}</button>
              </td>
              <td>
                <button type="button" aria-label={`delete-${d.reportCode}`} disabled={busy}
                  onClick={() => deleteReport(d.reportDefinitionId)}>{t('m16.catalog.deleteBtn')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </main>
  );
}
